import React, { useState, useEffect } from "react";
import { Shield, Lock, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Footer } from "./Footer";
import { Header } from "./Header";
import { getWithoutTokenAPI } from "../util/Api";

const staticSections = [
  {
    title: "1. Introduction",
    content:
      "This Privacy Policy explains how Aishwarya Matrimony collects, uses, shares, and protects the personal data of users accessing our matrimony services. By using Aishwarya Matrimony, you consent to this Privacy Policy.",
  },
  {
    title: "2. Information We Collect",
    list: [
      "Full Name, Gender, Date of Birth",
      "Phone Number, Email Address",
      "Marital Status, Religion & Caste",
      "Education and Employment details",
      "Location (city, state, country)",
      "Photos uploaded by the user",
      "Partner preferences",
    ],
  },
  {
    title: "3. How We Use Your Information",
    list: [
      "Create and manage your account",
      "Display your profile to other verified users",
      "Provide chat and connection features",
      "Process subscription payments",
      "Send notifications related to matches and messages",
      "Prevent fraud and unauthorized use",
    ],
  },
  {
    title: "4. Data Security",
    content:
      "We use industry-standard measures such as secure servers, encryption for sensitive data, and access control for internal staff. However, we cannot guarantee 100% security from unauthorized access.",
  },
  {
    title: "5. User Rights",
    list: [
      "View and edit your profile information",
      "Delete photos or your entire account",
      "Withdraw consent",
      "Report misuse",
    ],
  },
  {
    title: "6. Changes to Policy",
    content:
      "We may update this Privacy Policy occasionally. The latest version will always be displayed on our Platform. Continued use after changes means acceptance of the updated policy.",
  },
];

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [privacyData, setPrivacyData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPrivacy = async () => {
      try {
        const response = await getWithoutTokenAPI("/privacy-policy/get_all");
        if (response?.statusCode === 200 && response.data) {
          const data = Array.isArray(response.data)
            ? response.data[0]
            : response.data;
          if (data && !data.is_deleted) {
            setPrivacyData(data);
          }
        }
      } catch (error) {
        console.error("Error fetching privacy policy:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPrivacy();
  }, []);

  return (
    <>
      <Header />

      {/* Back link */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate("/")}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-base font-medium">Back</span>
            </button>
          </div>
        </div>
      </div>

      {/* Page Content */}
      <div style={{minHeight: "100vh", paddingTop: "2rem"}}>
        <div
          style={{
            width: "90%",
            maxWidth: "900px",
            margin: "0 auto 4rem auto",
            backgroundColor: "white",
            padding: "3rem",
            borderRadius: "16px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
          }}
        >
          {/* Header */}
          <div
            style={{
              textAlign: "center",
              marginBottom: "2.5rem",
              paddingBottom: "2rem",
              borderBottom: "2px solid #fee2e2",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "1rem",
              }}
            >
              <div
                style={{
                  padding: "12px",
                  borderRadius: "12px",
                  background: "linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))",
                  color: "white",
                  flexShrink: 0,
                }}
              >
                <Shield size={28} />
              </div>
              <h1
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "700",
                  background: "linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  margin: 0,
                  lineHeight: 1.2,
                }}
              >
                {privacyData?.title || "Privacy Policy"}
              </h1>
            </div>
            <p style={{ fontSize: "0.95rem", color: "#888" }}>
              Effective Date:{" "}
              {new Date().toLocaleDateString("en-IN", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>

          {/* Notice Banner */}
          <div
            style={{
              backgroundColor: "#dbeafe",
              borderLeft: "4px solid #3b82f6",
              padding: "1.25rem",
              borderRadius: "8px",
              marginBottom: "2.5rem",
              display: "flex",
              gap: "12px",
              alignItems: "flex-start",
            }}
          >
            <Lock size={20} color="#1e40af" style={{ flexShrink: 0, marginTop: "2px" }} />
            <p style={{ fontSize: "0.95rem", color: "#1e40af", margin: 0, fontWeight: "500", lineHeight: "1.6" }}>
              We are committed to protecting your privacy. Your data is secured with
              industry-standard encryption and access controls.
            </p>
          </div>

          {/* Dynamic Content from API */}
          {loading ? (
            <div style={{ textAlign: "center", padding: "2rem", color: "#9ca3af" }}>Loading...</div>
          ) : privacyData?.content ? (
            <div style={{ fontSize: "1rem", lineHeight: "1.8", color: "#374151" }}>
              {privacyData.content.includes("<") ? (
                <div dangerouslySetInnerHTML={{ __html: privacyData.content }} />
              ) : (
                <p style={{ whiteSpace: "pre-wrap" }}>{privacyData.content}</p>
              )}
            </div>
          ) : (
            /* Fallback static content */
            <div>
              {staticSections.map((section, index) => (
                <div
                  key={index}
                  style={{
                    padding: "1.5rem 0",
                    borderBottom: index === staticSections.length - 1 ? "none" : "1px solid #f3f4f6",
                  }}
                >
                  <h2
                    style={{
                      fontSize: "1.15rem",
                      fontWeight: "600",
                      background: "linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      marginBottom: "0.75rem",
                    }}
                  >
                    {section.title}
                  </h2>
                  {section.content && (
                    <p style={{ fontSize: "1rem", lineHeight: "1.7", color: "#4b5563", margin: 0 }}>
                      {section.content}
                    </p>
                  )}
                  {section.list && (
                    <ul style={{ paddingLeft: "1.5rem", margin: "0.5rem 0 0" }}>
                      {section.list.map((item, idx) => (
                        <li key={idx} style={{ fontSize: "1rem", lineHeight: "1.7", color: "#4b5563", marginBottom: "0.4rem" }}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;
